import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, DollarSign, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

function AnimatedNumber({ value, prefix = "", suffix = "" }: { value: number; prefix?: string; suffix?: string }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      setCurrent(Math.min(Math.floor(increment * step), value));
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <span className="tabular-nums">
      {prefix}{current.toLocaleString("pt-BR")}{suffix}
    </span>
  );
}

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
  },
};

export function LandingHero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#f8f9fa] pt-16">
      <div className="container relative mx-auto px-6 sm:px-12 lg:px-20 xl:px-28 pt-4 sm:pt-8 lg:pt-12 pb-12 sm:pb-20 lg:pb-28 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left content */}
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="text-center lg:text-left"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#e8eaf6] text-[#3949ab] text-sm font-medium mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#3949ab]" />
              Para freelancers que querem clareza sobre o próprio trabalho
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-display text-2xl sm:text-3xl lg:text-4xl xl:text-[2.75rem] font-normal tracking-wide leading-[1.15] mb-5"
            >
              <span className="text-[#1E40AF]">Você não precisa de mais clientes. </span>
              <span className="text-[#111111]">Precisa parar de trabalhar no escuro.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-sm sm:text-base text-[#333333] max-w-xl mx-auto lg:mx-0 mb-4 leading-relaxed"
            >
              O FreelaDash ajuda você a acompanhar tempo, projetos, clientes e financeiro pra entender, de forma clara, como está o seu trabalho.
              <br className="hidden sm:block" />
              Sem planilhas. Sem confusão. Sem achismo.
            </motion.p>

            <motion.div variants={fadeUp} className="text-sm text-[#444444] max-w-xl mx-auto lg:mx-0 mb-5">
              <p className="font-medium text-[#111111] mb-2 text-sm">Com o FreelaDash, você consegue:</p>
              <ul className="space-y-1 text-left list-none">
                {[
                  "Registrar suas horas de trabalho",
                  "Organizar projetos e clientes",
                  "Acompanhar o financeiro do mês",
                  "Ter uma visão real do que está acontecendo",
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    variants={{
                      hidden: { opacity: 0, x: -10 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: { delay: i * 0.08, duration: 0.4, ease: "easeOut" },
                      },
                    }}
                    className="flex items-start gap-2"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#111111] mt-2 shrink-0" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="text-xs sm:text-sm text-[#555555] max-w-xl mx-auto lg:mx-0 mb-6"
            >
              Nada automático. Você acompanha. O FreelaDash organiza.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start"
            >
              <Link to="/signup">
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Button
                    size="lg"
                    className="h-12 px-7 text-base bg-[#2563EB] hover:bg-[#1E40AF] text-white shadow-lg shadow-[#2563EB]/20 gap-2 rounded-xl transition-all duration-200"
                  >
                    Testar grátis por 7 dias
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="mt-3 text-xs text-[#666666] flex items-center justify-center lg:justify-start gap-4"
            >
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[#666666]" />
                Sem cartão de crédito
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-[#666666]" />
                Configuração em 3 minutos
              </span>
            </motion.p>
          </motion.div>

          {/* Right - Dashboard Mockup */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.4 }}
            className="relative"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }}
              whileHover={{ y: -4, boxShadow: "0 20px 40px rgba(0,0,0,0.08)" }}
              className="relative bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 shadow-lg transition-shadow duration-300"
            >
              {/* Header */}
              <div className="mb-5">
                <p className="text-[#666666] text-sm">Receita do mês</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#111111] flex items-baseline gap-1">
                  R$ <AnimatedNumber value={12450} />
                  <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.2, duration: 0.4, type: "spring" }}
                    className="text-emerald-500 text-sm ml-2 flex items-center gap-1"
                  >
                    <TrendingUp className="h-3.5 w-3.5" />
                    +23%
                  </motion.span>
                </p>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-5">
                {[
                  { icon: Clock, label: "Horas", value: 142, suffix: "h", prefix: "" },
                  { icon: DollarSign, label: "Valor/hora", value: 87, suffix: "", prefix: "R$ " },
                  { icon: Calendar, label: "Projeção", value: 15799, suffix: "", prefix: "R$ " },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + i * 0.12, duration: 0.5, ease: "easeOut" }}
                    className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-100"
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <stat.icon className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-[#666666]">{stat.label}</span>
                    </div>
                    <p className="text-lg sm:text-xl font-bold text-[#111111]">
                      {stat.prefix}<AnimatedNumber value={stat.value} />{stat.suffix}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* Chart simulation */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-[#444444] font-medium">Receita vs Meta</span>
                  <motion.span
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.4, duration: 0.4 }}
                    className="text-xs text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full font-medium"
                  >
                    78% da meta
                  </motion.span>
                </div>
                <div className="flex items-end gap-2 h-20">
                  {[40, 55, 45, 70, 60, 85, 78].map((height, i) => (
                    <motion.div
                      key={i}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: `${height}%`, opacity: 1 }}
                      transition={{
                        delay: 0.9 + i * 0.1,
                        duration: 0.6,
                        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
                      }}
                      className={`flex-1 rounded-t ${
                        i === 6 ? "bg-[#2563EB]" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex justify-between mt-2 text-xs text-[#888888]">
                  <span>Seg</span>
                  <span>Ter</span>
                  <span>Qua</span>
                  <span>Qui</span>
                  <span>Sex</span>
                  <span>Sáb</span>
                  <span>Dom</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
